import React from 'react';
import { Link } from 'react-router-dom';
import { SCREENS } from '../../../services/exports/Constants';
import useNavigate from '../../../hooks/navigation/useNavigate';
import { Props } from './GiftCardTermsAndConditions';

const En = (props: Props) => {
  const { generatePath } = useNavigate();
  const {
    companyLegalName,
    companyAddress,
    companyLegalEmail,
    companyLegalWebsite,
    companyLegalBusinessName,
    companyLegalPrivacyUrl,
    agbVersionDate,
    companyLegalCity,
    companyCountryName,
    currencyName,
  } = props;

  return (
    <article className="mx-auto w-full max-w-4xl px-md pb-md">
      <header className="py-md bg-white">
        <h1 className="text-2xl font-semibold leading-tight">
          Terms and Conditions for the Sale of {companyLegalName} Gift Cards
        </h1>
        <p className="mt-2 text-sm text-slate-600">As of: {agbVersionDate}</p>
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-medium text-slate-900">{companyLegalName}</p>
          <p className="text-sm text-slate-700">{companyAddress}</p>
          <div className="mt-3 flex flex-col gap-1 text-sm text-slate-700">
            <span>
              Email:&nbsp;
              <a className="underline underline-offset-2 hover:text-slate-900" href={`mailto:${companyLegalEmail}`}>
                {companyLegalEmail}
              </a>
            </span>
            <span>
              Website:&nbsp;
              <a
                className="underline underline-offset-2 hover:text-slate-900"
                href={companyLegalWebsite}
                target="_blank"
                rel="noreferrer"
              >
                {companyLegalWebsite}
              </a>
            </span>
          </div>
        </div>
      </header>

      <div className="prose prose-slate max-w-none space-y-sm">
        <h2>1. Scope and Contracting Party</h2>
        <p>
          <strong>1.1.</strong> These Terms and Conditions (“T&amp;Cs”) apply to the sale of gastronomy gift cards
          (“gift cards”) by {companyLegalName}, {companyAddress}.
        </p>
        <p>
          <strong>1.2.</strong> The purchase agreement for the gift card is concluded between you and {companyLegalName}
          .
          <br />
          <strong>Contact</strong>
          <br />
          Email: {companyLegalEmail}
          <br />
          Website: {companyLegalWebsite}
        </p>
        <p>
          <strong>1.3.</strong> Deviating terms and conditions of the customer are not recognized unless{' '}
          {companyLegalBusinessName} expressly agrees to their validity in writing.
        </p>

        <h2>2. Conclusion of Contract</h2>
        <p>
          <strong>2.1.</strong> The presentation of gift cards in the online shop of {companyLegalBusinessName} does not
          constitute a legally binding offer, but a non-binding invitation to order gift cards.
        </p>
        <p>
          <strong>2.2.</strong> Before submitting your order, all entries are displayed in an order overview and can be
          corrected there. Only by clicking the “Buy now” button do you submit a binding offer to conclude a purchase
          agreement for the gift cards in the shopping cart.
        </p>
        <p>
          <strong>2.3.</strong> After submitting the order, you will receive an automatic order confirmation by email.
          The purchase agreement is concluded upon receipt of this order confirmation.
        </p>
        <p>
          <strong>2.4.</strong> The contract text is not permanently stored by {companyLegalBusinessName} in the
          customer account. You will receive the order data as well as these T&amp;Cs as a PDF by email and can also
          view, save and print them on the website of {companyLegalBusinessName}.
        </p>
        <p>
          <strong>2.5.</strong> The contract language is German.
        </p>

        <h2>3. The {companyLegalName} Gift Card</h2>
        <p>
          <strong>3.1.</strong> The gift cards issued by {companyLegalBusinessName} entitle the holder to purchase food
          and beverages prepared in the {companyLegalName} venues operated by {companyLegalBusinessName} up to the face
          value stated on the gift card.
        </p>
        <p>
          <strong>3.2.</strong> Unless expressly stated otherwise, the gift card may be used exclusively to pay for
          gastronomic services (food and beverages). The purchase of other goods or services with the gift card is only
          permitted if expressly allowed by {companyLegalBusinessName}.
        </p>
        <p>
          <strong>3.3.</strong> Unless otherwise indicated during the ordering process, gift cards can be redeemed at
          all locations operated by {companyLegalBusinessName}. In case of doubt, whether and which locations
          participate can be found on the website {companyLegalWebsite} or in the information on the gift card.
        </p>
        <p>
          <strong>3.4.</strong> Gift cards are valid for three years, starting from the end of the calendar year in
          which the gift card was purchased, unless a different period is stated on the gift card or in the ordering
          process.
        </p>
        <p>
          <strong>3.5.</strong> A payout of the gift card’s face value in cash is excluded—subject to any mandatory
          statutory claims.
        </p>
        <p>
          <strong>3.6.</strong> The gift card is transferable. {companyLegalBusinessName} is entitled, but not obliged,
          to verify the material entitlement of the redeemer beyond the mere presentation of the gift card.
        </p>

        <h2>4. Gift Cards as Gifts</h2>
        <p>
          <strong>4.1.</strong> Gift cards can be given to third parties as gifts and redeemed by them in accordance
          with the gift card terms.
        </p>
        <p>
          <strong>4.2.</strong> The current balance of a gift card results from the redemptions of the gift card at the
          {companyLegalName} venue. A right to information about the balance exists only within the technical
          capabilities of {companyLegalBusinessName}.
        </p>

        <h2>5. Redeeming the Gift Card</h2>
        <p>
          <strong>5.1.</strong> To redeem the gift card, it must be presented (in paper form or as a PDF/code on a
          smartphone) at the {companyLegalName} venue. The service staff records the gift card code in the checkout
          system and applies the gift card amount to your bill.
        </p>
        <p>
          <strong>5.2.</strong> If the value of the gift card is not sufficient to cover the entire bill, the remaining
          amount can be paid using the payment methods offered at the {companyLegalName} venue.
        </p>
        <p>
          <strong>5.3.</strong> If the gift card value exceeds the bill amount, any remaining balance may—if technically
          possible—remain stored on the gift card and be used on a later visit. If this is not possible, the difference
          will not be paid out in cash unless mandatory legal provisions require otherwise.
        </p>
        <p>
          <strong>5.4.</strong> In the event of loss, theft or misuse of the gift card, {companyLegalBusinessName}{' '}
          assumes no liability for unauthorized use of the gift card. Therefore, treat your gift card like cash.
        </p>

        <h2>6. Delivery</h2>
        <p>
          <strong>6.1.</strong> Depending on the selection made during the ordering process, {companyLegalBusinessName}{' '}
          provides the gift card either in electronic form (by email as a PDF with gift card code) or in physical form
          (e.g., a gift card sent by post).
        </p>
        <p>
          <strong>6.2.</strong> Electronic gift cards are generally sent to the email address you provided within one
          business day after conclusion of the contract.
        </p>
        <p>
          <strong>6.3.</strong> If shipping of physical gift cards is offered, delivery is made exclusively to addresses
          within {companyCountryName}. Unless otherwise stated, delivery takes up to five business days from conclusion
          of the contract. Business days are all days from Monday to Friday excluding public holidays at the registered
          office of {companyLegalBusinessName}.
        </p>

        <h2>7. Prices and Payment Terms</h2>
        <p>
          <strong>7.1.</strong> All prices are total prices in {currencyName}, including the applicable statutory VAT,
          unless expressly stated otherwise.
        </p>
        <p>
          <strong>7.2.</strong> Any additional shipping costs may apply and will be shown separately during the ordering
          process.
        </p>
        <p>
          <strong>7.3.</strong> The payment methods accepted in the online shop (e.g., credit card, PayPal, instant bank
          transfer) are displayed during the ordering process. {companyLegalBusinessName} reserves the right to exclude
          certain payment methods in individual cases.
        </p>
        <p>
          <strong>7.4.</strong> The purchase price is due immediately upon conclusion of the contract.
        </p>

        <h2>8. Data Protection</h2>
        <p>
          <strong>8.1.</strong> {companyLegalBusinessName} processes personal data in connection with gift card orders
          exclusively in accordance with applicable data protection regulations.
        </p>
        <p>
          <strong>8.2.</strong> Further information on data processing can be found in the current privacy policy, which
          is available at&nbsp;
          <Link to={generatePath(SCREENS.PRIVACY_POLICY)} target="_blank" className="underline">
            {companyLegalPrivacyUrl}
          </Link>
          .
        </p>

        <h2>9. Right of Withdrawal for Consumers – Physical Gift Cards</h2>
        <p>
          <strong>9.1.</strong> If you are a consumer and purchase a physical gift card (e.g., a gift card sent by
          post), you have a statutory right of withdrawal, about which {companyLegalBusinessName} informs you as
          follows:
        </p>

        <div className="not-prose rounded-xl border border-slate-200 bg-slate-50 p-md">
          <p className="font-semibold">Withdrawal Notice for Physical Gift Cards</p>
          <p className="mt-2">
            You have the right to withdraw from this contract within fourteen days without giving any reason.
          </p>
          <p className="mt-2">
            The withdrawal period is fourteen days from the day on which you or a third party designated by you (who is
            not the carrier) took possession of the gift card.
          </p>
          <p className="mt-2">
            To exercise your right of withdrawal, you must inform us
            {companyLegalName}
            <br />
            {companyAddress}
            <br />
            Email: {companyLegalEmail}
            <br />
            by means of a clear statement (e.g., a letter sent by post or an email) of your decision to withdraw from
            this contract. You may use the sample withdrawal form attached below, but this is not mandatory.
          </p>
          <p className="mt-2">
            To meet the withdrawal deadline, it is sufficient for you to send the notification of exercising the right
            of withdrawal before the withdrawal period expires.
          </p>
          <p className="mt-2 font-semibold">Consequences of Withdrawal</p>
          <p className="mt-2">
            If you withdraw from this contract, we will reimburse all payments received from you, including delivery
            costs (with the exception of any additional costs resulting from your choice of a type of delivery other
            than the cheapest standard delivery offered by us), without undue delay and at the latest within fourteen
            days from the day on which we receive notice of your withdrawal from this contract.
          </p>
          <p className="mt-2">
            For this reimbursement, we will use the same means of payment that you used for the original transaction,
            unless expressly agreed otherwise with you; in no case will you be charged any fees for this reimbursement.
          </p>
          <p className="mt-2">
            We may refuse reimbursement until we have received the gift card back or you have provided evidence that you
            have returned the gift card, whichever is earlier.
          </p>
          <p className="mt-2">
            You must return or hand over the gift card to us without undue delay and in any event no later than fourteen
            days from the day on which you inform us of your withdrawal from this contract. The deadline is met if you
            send the gift card before the period of fourteen days has expired. We will bear the cost of returning the
            gift card.
          </p>
          <p className="mt-2">
            You are only liable for any diminished value of the gift card resulting from handling other than what is
            necessary to establish the nature, characteristics and functioning of the gift card.
          </p>
          <p className="mt-2">End of the Withdrawal Notice</p>
        </div>

        <h2>10. Right of Withdrawal for Consumers – Electronic Gift Cards</h2>
        <p>
          <strong>10.1.</strong> If you are a consumer and purchase an electronic gift card (e.g., a PDF sent by email),
          you generally have a statutory right of withdrawal.
        </p>
        <p>
          <strong>10.2.</strong> However, the right of withdrawal expires if {companyLegalBusinessName}—with your
          express consent—has begun performing the contract before the expiry of the withdrawal period and has provided
          the gift card, and you simultaneously confirmed that you lose your right of withdrawal once performance
          begins. Otherwise, withdrawals are handled in accordance with statutory provisions; in case of doubt, the
          provisions of Section 9 apply accordingly.
        </p>

        <h2>11. Sample Withdrawal Form</h2>
        <div className="not-prose rounded-xl border border-slate-200 bg-white p-md">
          <p className="font-semibold">
            (If you wish to withdraw from the contract, please complete this form and return it.)
          </p>
          <p className="mt-3">
            To
            <br />
            {companyLegalName}
            <br />
            {companyAddress}
            <br />
            Email: {companyLegalEmail}
          </p>

          <div className="mt-4 space-y-4 text-sm text-slate-800">
            <div>
              <p>I hereby withdraw from the contract concluded by me for the purchase of the following gift card:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Ordered on:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Name of the consumer:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Address of the consumer:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Signature of the consumer (only if communicated on paper)</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
            <div>
              <p>Date:</p>
              <div className="mt-2 h-10 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
            </div>
          </div>
        </div>

        <h2>12. Warranty, Liability, Complaints</h2>
        <p>
          <strong>12.1.</strong> The statutory warranty rights apply.
        </p>
        <p>
          <strong>12.2.</strong> Claims for damages are excluded unless there is a breach of essential contractual
          obligations (cardinal obligations), an injury to life, body or health, liability under the Product Liability
          Act, or other mandatory liability.
        </p>
        <p>
          <strong>12.3.</strong> For complaints or questions about the order, you can contact us by email at{' '}
          {companyLegalEmail}.
        </p>

        <h2>13. Applicable Law and Place of Jurisdiction</h2>
        <p>
          <strong>13.1.</strong> German law applies, excluding the UN Convention on Contracts for the International Sale
          of Goods (CISG). Mandatory consumer protection provisions of the country in which you have your habitual
          residence remain unaffected.
        </p>
        <p>
          <strong>13.2.</strong> If you are a merchant, a legal entity under public law, or a special fund under public
          law, the exclusive place of jurisdiction for all disputes arising from contractual relationships between you
          and {companyLegalBusinessName} is the registered office of {companyLegalCity}.
        </p>
      </div>
    </article>
  );
};

export default En;
